import {
  createContext,
  useContext,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Box } from './box';
import {
  FULL_FIT,
  PAGE_BLEED,
  PHONE_LANDSCAPE_QUERY,
  fitScreen,
  readVisibleHeight,
  screenLayout,
  stageCap,
  stageLayout,
  type ScreenFit,
  type ScreenLayout,
  type StageLayout,
} from '../../helpers/utils/screenFit';
import { keyboardInset } from '../../helpers/utils/keyboardReveal';
import { getScrollCompensation } from '../../helpers/utils/scrollRestoration';
import { screenRect } from '../../helpers/utils/scrollFrame';

// The fit rule's table height and scale (docs/long-names-code-todo.md, S3 /
// S4). Home and the Table board both sit in a FitStage, so they get the same
// H and s and nothing jumps on Start. On phones that's the board's budget,
// down to the latest past round. TableSurface reads H from here.
const ScreenFitContext = createContext<ScreenFit>(FULL_FIT);

export const useScreenFit = () => useContext(ScreenFitContext);

/** Phones bleed the page out of .App's padding (md is Chakra's 768). */
const PHONE_MAX_WIDTH = 767;

// 100svh is the height with the browser toolbars showing, and the keyboard
// doesn't change it. The safe-area inset covers the installed app.
const PROBE_STYLE =
  'position:fixed;top:0;left:0;width:0;visibility:hidden;pointer-events:none;' +
  'height:calc(100svh - env(safe-area-inset-bottom, 0px))';

interface FitState {
  fit: ScreenFit;
  stage: StageLayout | null;
  layout: ScreenLayout;
}

const NOT_TEXT = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

/** A field the on-screen keyboard opens for. */
const isTextEntry = (el: Element | null) =>
  el instanceof HTMLTextAreaElement ||
  (el instanceof HTMLInputElement && !NOT_TEXT.has(el.type)) ||
  (el instanceof HTMLElement && el.isContentEditable);

const sameState = (a: FitState, b: FitState) =>
  a.layout === b.layout &&
  a.fit.tableHeight === b.fit.tableHeight &&
  a.fit.scale === b.fit.scale &&
  JSON.stringify(a.stage) === JSON.stringify(b.stage);

// "Table spotlight · felt glow" (Figma 1728:103870): a felt ellipse blurred
// by 90pt behind the stage, in screen points. Its centre sits this far below
// the stage's top, in design points, so it follows the table as s changes.
const GLOW = {
  tablet: { width: 760, height: 700, centreY: 321 },
  desktop: { width: 900, height: 760, centreY: 306 },
} as const;
const GLOW_BLUR = 90;

// Painted as a radial gradient, not with filter: blur(). After the round-end
// animation, the user's iPad (Brave, 2026-10-28) redrew parts of the blurred
// glow unblurred: a hard-edged slab around the board's box and a strip down
// the left edge. A gradient paints the same on every redraw. It fills a box
// GLOW_SPREAD times the ellipse, so the ellipse's edge sits at 1 / GLOW_SPREAD
// of the gradient, and the blur's falloff (a normal curve) spans it: each
// stop is [distance past the edge in blur radii, share of the colour left].
// The box is SVG content overflowing a 1px <svg>: it paints, but only the
// 1px counts toward the page's size, so the glow never adds scrolling.
const GLOW_SPREAD = 1.7;
const GLOW_FALLOFF = [
  [-3, 0.99865],
  [-2, 0.97725],
  [-1.5, 0.93319],
  [-1, 0.84134],
  [-0.5, 0.69146],
  [0, 0.5],
  [0.5, 0.30854],
  [1, 0.15866],
  [1.5, 0.06681],
  [2, 0.02275],
  [3, 0.00135],
] as const;

function FeltGlow({
  layout,
  scale,
}: {
  layout: 'tablet' | 'desktop';
  scale: number;
}) {
  const id = `${useId().replace(/:/g, '')}-glow`;
  const { width, height, centreY } = GLOW[layout];
  // Design points: the stage's zoom scales them back to screen points.
  const boxWidth = (width * GLOW_SPREAD) / scale;
  const boxHeight = (height * GLOW_SPREAD) / scale;
  // The blur as a share of the gradient's mean radius.
  const blur = GLOW_BLUR / ((GLOW_SPREAD * (width + height)) / 4);
  return (
    <svg
      aria-hidden="true"
      data-testid="felt-glow"
      width="1"
      height="1"
      style={{
        position: 'absolute',
        zIndex: -1,
        pointerEvents: 'none',
        overflow: 'visible',
        left: 'calc(50% - 0.5px)',
        top: `${centreY - 0.5}px`,
      }}
    >
      <defs>
        <radialGradient id={id}>
          {GLOW_FALLOFF.map(([distance, share]) => (
            <stop
              key={distance}
              offset={Math.min(1, 1 / GLOW_SPREAD + distance * blur)}
              stopColor="var(--spades-felt-dark)"
              stopOpacity={0.55 * share}
            />
          ))}
        </radialGradient>
      </defs>
      <rect
        x={0.5 - boxWidth / 2}
        y={0.5 - boxHeight / 2}
        width={boxWidth}
        height={boxHeight}
        fill={`url(#${id})`}
      />
    </svg>
  );
}

interface FitStageProps {
  children: ReactNode;
}

/**
 * The page below the header. At ×1 in portrait it's a plain block. On a tiny
 * phone it zooms the 390 design by s and centres it (Figma section 12). In
 * phone landscape it centres the portrait layout at ×1 and the page scrolls.
 * On tablets and desktops it zooms the 390 design by up to ×1.6 / ×1.42
 * (T1 / D1) and centres it in its column, over a felt glow.
 */
function FitStage({ children }: FitStageProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<FitState>({
    fit: FULL_FIT,
    stage: null,
    layout: 'phone',
  });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const probe = document.createElement('div');
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText = PROBE_STYLE;
    document.body.appendChild(probe);
    const landscape = window.matchMedia?.(PHONE_LANDSCAPE_QUERY);

    let measuredWidth: number | null = null;
    let editHeight: number | null = null;
    let closingKeyboard = false;
    let afterEdit: number | undefined;
    const measure = () => {
      const parent = el.parentElement;
      if (!parent) return;
      // Typing never refits the screen: a keyboard can shrink the window
      // (Android resizes-content, iOS 26) and iOS misreports scroll while it's
      // up, and either would zoom the stage under the user's finger. Only a
      // width change (rotation) refits mid-edit.
      const typing =
        isTextEntry(document.activeElement) ||
        keyboardInset({
          innerHeight: window.innerHeight,
          viewportHeight: window.visualViewport?.height,
          viewportScale: window.visualViewport?.scale,
        }) > 0;
      if (window.innerWidth === measuredWidth) {
        if (typing) return;
        if (
          closingKeyboard &&
          editHeight !== null &&
          window.innerHeight < editHeight - 2
        ) {
          window.clearTimeout(afterEdit);
          afterEdit = window.setTimeout(() => {
            closingKeyboard = false;
            measure();
          }, 400);
          return;
        }
      }
      closingKeyboard = false;
      measuredWidth = window.innerWidth;
      const isPhoneLandscape = landscape?.matches ?? false;
      const layout = screenLayout({
        width: window.innerWidth,
        isPhoneLandscape,
      });
      const fit = fitScreen({
        visible: readVisibleHeight({
          svh: probe.offsetHeight,
          visualViewport: window.visualViewport?.height,
          innerHeight: window.innerHeight,
        }),
        // The stage's offset in the document, from two rects in the same
        // frame: no scrollY, which iOS reports oddly with the keyboard up.
        // Native-offset compensation fills clipped space above the viewport;
        // it must not reduce the usable height or squash the table.
        top:
          screenRect(el).top -
          document.documentElement.getBoundingClientRect().top -
          getScrollCompensation(),
        isPhoneLandscape,
        maxScale: stageCap({ layout, width: window.innerWidth }),
        layout,
      });
      const style = getComputedStyle(parent);
      const available =
        parent.clientWidth -
        parseFloat(style.paddingLeft || '0') -
        parseFloat(style.paddingRight || '0');
      const bleed = window.innerWidth <= PHONE_MAX_WIDTH ? 2 * PAGE_BLEED : 0;
      const next: FitState = {
        fit,
        layout,
        stage: stageLayout({
          width: available + bleed,
          available,
          scale: fit.scale,
          isPhoneLandscape,
          layout,
        }),
      };
      setState((prev) => (sameState(prev, next) ? prev : next));
    };

    measure();
    // A blur can precede the keyboard closing. Hold the fit through its
    // resize events, then catch up when it returns or resizing settles.
    const onFocusIn = (event: FocusEvent) => {
      if (isTextEntry(event.target as Element)) {
        if (!closingKeyboard) editHeight = window.innerHeight;
        closingKeyboard = false;
        window.clearTimeout(afterEdit);
      }
    };
    const onFocusOut = (event: FocusEvent) => {
      if (!isTextEntry(event.target as Element)) return;
      closingKeyboard = true;
      window.clearTimeout(afterEdit);
      afterEdit = window.setTimeout(() => {
        closingKeyboard = false;
        measure();
      }, 400);
    };
    // Not visualViewport resize: the keyboard must not refit the screen.
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    landscape?.addEventListener?.('change', measure);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      landscape?.removeEventListener?.('change', measure);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      window.clearTimeout(afterEdit);
      probe.remove();
    };
  }, []);

  const { fit, stage, layout } = state;

  return (
    <ScreenFitContext.Provider value={fit}>
      <Box
        ref={ref}
        data-testid="fit-stage"
        // A new formatting context, so the first child's top margin stays
        // inside and the measured top doesn't move with the zoom.
        display="flow-root"
        data-fit-scale={fit.scale}
        data-table-height={fit.tableHeight}
        data-layout={layout}
        // The glow sits behind the content, inside the stage.
        {...(layout !== 'phone' && {
          position: 'relative',
          isolation: 'isolate',
        })}
        {...(stage && {
          // The content steps back out of this padding (mx -8 on phones), so
          // it's exactly stage.width wide.
          width: `${stage.width}px`,
          px: { base: `${PAGE_BLEED}px`, md: 0 },
          style: { zoom: stage.zoom, marginLeft: `${stage.marginLeft}px` },
        })}
      >
        {layout !== 'phone' && stage && (
          <FeltGlow layout={layout} scale={fit.scale} />
        )}
        {children}
      </Box>
    </ScreenFitContext.Provider>
  );
}

export default FitStage;
